import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { track, trackPageOnce } from "@/lib/analytics";

import Landing from "@/pages/Landing";
import { Login, Register, Forgot, Reset } from "@/pages/Auth";
import UserLayout from "@/layouts/UserLayout";
import AdminLayout from "@/layouts/AdminLayout";

import Dashboard from "@/pages/user/Dashboard";
import Profile from "@/pages/user/Profile";
import Deposit from "@/pages/user/Deposit";
import IncomeDetail from "@/pages/user/IncomeDetail";
import Withdrawal from "@/pages/user/Withdrawal";
import WithdrawalHistory from "@/pages/user/WithdrawalHistory";
import DepositHistory from "@/pages/user/DepositHistory";
import Support from "@/pages/user/Support";
import Binary from "@/pages/user/Binary";
import Directs from "@/pages/user/Directs";
import Team from "@/pages/user/Team";
import Rewards from "@/pages/user/Rewards";
import Salary from "@/pages/user/Salary";
import Ranks from "@/pages/user/Ranks";
import Transactions from "@/pages/user/Transactions";
import Notifications from "@/pages/user/Notifications";
import Security from "@/pages/user/Security";
import PinkPinActivate from "@/pages/user/PinkPinActivate";
import Trading from "@/pages/user/Trading";
import CommissionTree from "@/pages/user/CommissionTree";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import VirtualBusiness from "@/pages/admin/VirtualBusiness";
import AdminBinary from "@/pages/admin/AdminBinary";
import AdminDeposits from "@/pages/admin/AdminDeposits";
import AdminWithdrawals from "@/pages/admin/AdminWithdrawals";
import AdminLedger from "@/pages/admin/AdminLedger";
import AdminConfig from "@/pages/admin/AdminConfig";
import AdminCrypto from "@/pages/admin/AdminCrypto";
import AdminSendFund from "@/pages/admin/AdminSendFund";
import Activate from "@/pages/user/Activate";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminGlobalPools from "@/pages/admin/AdminGlobalPools";
import AdminPoolWithdrawals from "@/pages/admin/AdminPoolWithdrawals";
import AdminNews from "@/pages/admin/AdminNews";
import AdminDepositVerify from "@/pages/admin/AdminDepositVerify";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminPinkPins from "@/pages/admin/AdminPinkPins";
import AdminSecurityCenter from "@/pages/admin/AdminSecurityCenter";
import AdminAIAgent from "@/pages/admin/AdminAIAgent";
import AdminSignBanner from "@/pages/admin/AdminSignBanner";
import AdminTrading from "@/pages/admin/AdminTrading";
import AdminSecrets from "@/pages/admin/AdminSecrets";

const ADMIN_ROLES = ["super_admin", "admin", "manager", "support"];

function FullLoader() {
  return <div className="grid min-h-screen place-items-center bg-[var(--bg)]"><Loader2 className="animate-spin text-[var(--gold)]" size={32} /></div>;
}

function Protected({ admin = false, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = ADMIN_ROLES.includes(user.role);
  if (admin && !isAdmin) return <Navigate to="/app" replace />;
  if (!admin && isAdmin) return <Navigate to="/admin" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (user) return <Navigate to={ADMIN_ROLES.includes(user.role) ? "/admin" : "/app"} replace />;
  return children;
}

function AnalyticsTracker() {
  const loc = useLocation();
  React.useEffect(() => {
    trackPageOnce();
    if (loc.pathname === "/register") track("registration_started");
    if (loc.pathname === "/app/deposit") track("deposit_page_view");
  }, [loc.pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<Register />} />
      <Route path="/signup" element={<Register />} />
      <Route path="/forgot-password" element={<Forgot />} />
      <Route path="/reset-password" element={<Reset />} />

      <Route path="/app" element={<Protected><UserLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="trading" element={<Trading />} />
        <Route path="self-trade/commission" element={<CommissionTree />} />
        <Route path="profile" element={<Profile />} />
        <Route path="deposit" element={<Deposit />} />
        <Route path="deposit/history" element={<DepositHistory />} />
        <Route path="activate" element={<Activate />} />
        <Route path="pink-pin" element={<PinkPinActivate />} />
        <Route path="income/:kind" element={<IncomeDetail />} />
        <Route path="withdrawal" element={<Withdrawal />} />
        <Route path="withdrawal/history" element={<WithdrawalHistory />} />
        <Route path="support" element={<Support />} />
        <Route path="binary" element={<Binary />} />
        <Route path="directs" element={<Directs />} />
        <Route path="team" element={<Team />} />
        <Route path="rewards" element={<Rewards />} />
        <Route path="salary" element={<Salary />} />
        <Route path="ranks" element={<Ranks />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="security" element={<Security />} />
      </Route>

      <Route path="/admin" element={<Protected admin><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="secrets-vault" element={<AdminSecrets />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="virtual-business" element={<VirtualBusiness />} />
        <Route path="binary" element={<AdminBinary />} />
        <Route path="deposits" element={<AdminDeposits />} />
        <Route path="deposit-verify" element={<AdminDepositVerify />} />
        <Route path="withdrawals" element={<AdminWithdrawals />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="global-pools" element={<AdminGlobalPools />} />
        <Route path="pool-payouts" element={<AdminPoolWithdrawals />} />
        <Route path="news" element={<AdminNews />} />
        <Route path="send-fund" element={<AdminSendFund />} />
        <Route path="ledger" element={<AdminLedger />} />
        <Route path="config" element={<AdminConfig />} />
        <Route path="crypto" element={<AdminCrypto />} />
        <Route path="security" element={<AdminSecurity />} />
        <Route path="marketing" element={<AdminMarketing />} />
        <Route path="pink-pins" element={<AdminPinkPins />} />
        <Route path="security-center" element={<AdminSecurityCenter />} />
        <Route path="ai-agent" element={<AdminAIAgent />} />
        <Route path="sign-banner" element={<AdminSignBanner />} />
        <Route path="trading" element={<AdminTrading />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <AnalyticsTracker />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
